<template>
  <Wrapper class="q-px-lg q-py-xl">
    <BackButton />
    <h5 class="q-md-mb">
      {{ isEditing ? 'Edit FAQ' : 'Create new FAQ' }}
    </h5>

    <div class="row q-mb-md q-col-gutter-md items-center">
      <div class="col-6">
        <q-input
          v-model="title"
          label="Title"
          error-message="Title is required"
          no-error-icon
          :error="getErrorState($v.title)"
        />
      </div>

      <div class="col-6">
        <q-select
          v-model="selectedTopic"
          label="Topic"
          error-message="Topic is required"
          no-error-icon
          :options="topics"
          :error="getErrorState($v.selectedTopic)"
          @filter="filterFn"
        >
        </q-select>
      </div>
    </div>

    <div class="col-12">
      <q-editor
        v-model="content"
        placeholder="Insert text here..."
        toolbar-text-color="black"
        toolbar-bg="toolbar"
        content-class="content"
        :toolbar="toolbar"
      />
      <p v-show="getErrorState($v.content)" class="text-negative q-mt-sm">Content is required</p>
    </div>

    <div v-show="keywords.length < 10" class="col-12">
      <q-input
        v-model.trim="keyword"
        label="Keywords"
        hint="Keywords (optional - maximum 10) Press enter to create new keyword"
        no-error-icon
        bottom-slots
        :error="getErrorState($v.keyword)"
        @keypress.enter="addKeyword"
      >
        <template #error>
          <span v-if="!$v.keyword.alpha">Keyword must contain characters only</span>
          <span v-else-if="!$v.keyword.maxLength">Keyword max length reached (255)</span>
          <span v-else-if="!$v.keyword.unique">Each keyword must be unique</span>
        </template>
      </q-input>
    </div>

    <div v-show="keywords.length" class="col-12 q-mt-md">
      <q-chip v-for="(key, idx) in keywords" :key="key" :label="key" removable @remove="keywords.splice(idx, 1)" />
    </div>

    <div class="col-12 q-gutter-x-sm text-right">
      <q-btn color="primary" label="Preview" @click="openPreview" />
      <q-btn
        color="muted"
        flat
        :disable="isLoading || $v.$invalid"
        :loading="isLoading"
        :label="isEditing ? 'Edit FAQ' : 'Create FAQ'"
        @click="faqAction"
      />
    </div>

    <!-- Preview Dialog -->
    <q-dialog v-model="isPreviewDialogOpen">
      <q-card style="width: 30rem; max-width: 80vw">
        <q-card-section tag="section">
          <h6>{{ title }}</h6>
        </q-card-section>

        <q-card-section tag="section">
          <p class="text-bold">Support - {{ selectedTopic }}</p>

          <div class="content" v-html="content" />
        </q-card-section>

        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Back" color="muted" :disable="isLoading" />
          <q-btn label="Confirm" color="primary" :loading="isLoading" @click="faqAction" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </Wrapper>
</template>

<script>
import required from 'vuelidate/lib/validators/required';
import alpha from 'vuelidate/lib/validators/alpha';
import maxLength from 'vuelidate/lib/validators/maxLength';
import { QEditor } from 'quasar';
import Wrapper from '@/components/Wrapper.vue';
import { getErrorMessage } from '@/utils';
import getErrorState from '@/utils/getErrorState';
import BackButton from '@/components/BackButton.vue';

export default {
  components: {
    BackButton,
    Wrapper,
    QEditor,
  },
  props: {
    slug: {
      type: String,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      title: '',
      content: '',
      selectedTopic: '',
      keyword: '',
      keywords: [],
      topics: null,
      error: undefined,
      isPreviewDialogOpen: false,
      isLoading: false,
      toolbar: [
        ['left', 'center', 'right', 'justify'],
        ['bold', 'italic', 'underline', 'strike'],
        ['link'],
        ['quote', 'unordered', 'ordered', 'outdent', 'indent'],
        [
          {
            label: this.$q.lang.editor.formatting,
            icon: this.$q.iconSet.editor.formatting,
            list: 'no-icons',
            options: ['p', 'h3', 'h4', 'h5', 'h6', 'code'],
          },
        ],
        ['undo', 'redo'],
      ],
    };
  },

  computed: {
    isEditing() {
      return this.$route.path.includes('edit');
    },
  },

  async created() {
    if (this.isEditing) {
      const { data } = await this.$axios.get(`/admin/faq/${this.slug}`);

      this.title = data.title;
      this.selectedTopic = data.topic && data.topic.name;
      this.content = data.content;
      this.keywords = data.keywords;
    }
  },

  methods: {
    getErrorState,
    openPreview() {
      this.$v.$reset();
      this.$v.$touch();

      if (this.$v.$invalid) return;

      this.isPreviewDialogOpen = true;
    },
    addKeyword() {
      this.$v.keyword.$touch();

      if (this.$v.keyword.$invalid || !this.keyword) return;

      this.keywords.push(this.keyword);
      this.keyword = '';
      this.$v.keyword.$reset();
    },
    async faqAction() {
      try {
        this.isLoading = true;
        this.error = undefined;
        this.$v.$reset();
        this.$v.$touch();

        if (this.$v.$invalid) return;

        const data = {
          title: this.title,
          topic: this.selectedTopic,
          content: this.content,
          keywords: this.keywords.length ? this.keywords : undefined,
        };

        if (this.isEditing) {
          await this.$axios.put(`/admin/faq/${this.slug}`, data);
        } else {
          await this.$axios.post('/admin/faq', data);
        }

        this.$q.notify({
          type: 'positive',
          message: this.isEditing ? `${this.title} has been edited!` : 'A new help article has been created!',
        });
        this.$router.back();
      } catch (error) {
        this.$q.notify({
          type: 'negative',
          message: getErrorMessage(error),
        });
      } finally {
        this.isLoading = false;
      }
    },
    async filterFn(val, update) {
      if (this.topics !== null) {
        // already loaded
        update();
        return;
      }

      const { data } = await this.$axios.get('/help/topics');

      update(() => {
        this.topics = data.map((i) => i.name);
      });
    },
  },

  validations: {
    title: {
      required,
    },
    selectedTopic: {
      required,
    },
    content: {
      required,
    },
    keyword: {
      alpha,
      maxLength: maxLength(255),
      unique: (value, vm) => !vm.keywords.includes(value),
    },
  },
};
</script>

<style>
#faq .bg-toolbar {
  background-color: #ebebeb;
  border-bottom: none;
}
#faq .content {
  background-color: #f6f6f6;
}

#faq .content ul {
  list-style: initial;
}
</style>
